import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Label,
  Input,
  Button,
  Form,
  Alert,
  Card,
  CardTitle,
  CardBody,
} from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import axios from "axios"; // for API calls
import { get, post } from "helpers/api_helper";
import { RESET_PASSWORD, USER_PROFILE } from "helpers/url_helper";
import { useNavigate } from "react-router-dom";

const ProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [resetMessage, setResetMessage] = useState("");
  const [resetError, setResetError] = useState("");
  const [loading, setLoading] = useState(false);

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
  } = useForm();

  const newPassword = watch("newPassword");
  const navigation = useNavigate();

  // 🔁 Fetch profile data on load
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await get(USER_PROFILE);
        setProfile(response.data);
      } catch (error) {
        console.error("Failed to fetch profile", error);
      }
    };
    fetchProfile();
  }, []);

  // 🔒 Reset password
  const onSubmit = async (data) => {
    setResetMessage("");
    setResetError("");
    setLoading(true);
    try {
      await post(RESET_PASSWORD, data);
      setResetMessage("Password reset successfully");
      reset();
    } catch (error) {
      setResetError(
        error.response?.data?.message || "Failed to reset password"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Row>
          <Card>
            <CardBody>
              <CardTitle tag="h5" className="mb-4">
                My Profile
              </CardTitle>

              <Row className="mb-3">
                <Col md={4}>
                  <Label>Name</Label>
                  <Input type="text" value={profile?.name || ""} readOnly />
                </Col>
                <Col md={4}>
                  <Label>Email</Label>
                  <Input type="email" value={profile?.email || ""} readOnly />
                </Col>
                <Col md={4}>
                  <Label>Role</Label>
                  <Input
                    type="text"
                    value={profile?.user_type || ""}
                    readOnly
                  />
                </Col>
              </Row>

              <CardTitle tag="h6" className="mt-4 mb-3">
                Reset Password
              </CardTitle>

              {resetError && <Alert color="danger">{resetError}</Alert>}
              {resetMessage && <Alert color="success">{resetMessage}</Alert>}

              <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="mb-3">
                  <Col md={4}>
                    <Label>Current Password</Label>
                    <Controller
                      name="currentPassword"
                      control={control}
                      rules={{ required: "Current password is required" }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="password"
                          placeholder="Enter current password"
                          invalid={!!errors.currentPassword}
                        />
                      )}
                    />
                    {errors.currentPassword && (
                      <span className="text-danger">
                        {errors.currentPassword.message}
                      </span>
                    )}
                  </Col>
                  <Col md={4}>
                    <Label>New Password</Label>
                    <Controller
                      name="newPassword"
                      control={control}
                      rules={{
                        required: "New password is required",
                        minLength: {
                          value: 6,
                          message: "Minimum 6 characters",
                        },
                      }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="password"
                          placeholder="Enter new password"
                          invalid={!!errors.newPassword}
                        />
                      )}
                    />
                    {errors.newPassword && (
                      <span className="text-danger">
                        {errors.newPassword.message}
                      </span>
                    )}
                  </Col>
                  <Col md={4}>
                    <Label>Confirm Password</Label>
                    <Controller
                      name="confirmPassword"
                      control={control}
                      rules={{
                        required: "Please confirm password",
                        validate: (value) =>
                          value === newPassword || "Passwords do not match",
                      }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="password"
                          placeholder="Confirm password"
                          invalid={!!errors.confirmPassword}
                        />
                      )}
                    />
                    {errors.confirmPassword && (
                      <span className="text-danger">
                        {errors.confirmPassword.message}
                      </span>
                    )}
                  </Col>
                </Row>

                <div className="text-end">
                  <Button color="primary" type="submit">
                    {loading ? "Updating..." : "Update Password"}
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Row>
      </div>
    </React.Fragment>
  );
};

export default ProfilePage;
